import { AlertTriangle, RotateCcw } from 'lucide-react';

/**
 * Shown when the Cycle Designer throws — most often on switching between Org
 * Chart and Custom stops with a design already in progress, where the anchor
 * changes underneath paths that were drawn against the old one.
 *
 * The design itself is safe: the draft autosaves to `degap_cycles.metadata`
 * (`designerDraft`), so reloading restores the last saved step, form and paths.
 * Saying that plainly is the whole point of this screen — a browser error page
 * tells the user nothing and implies their work is gone.
 */
export function DesignerErrorFallback({ error }: { error?: Error }) {
  return (
    <div className="p-8 max-w-2xl">
      <div className="flex items-start gap-3">
        <AlertTriangle className="h-6 w-6 text-coral-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <h2 className="text-lg font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)] mb-2">
            The designer stopped partway through
          </h2>
          <p className="text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-2">
            <strong>Your design is saved.</strong> It autosaves to the cycle as you
            work, so reloading picks it up at the last step you were on.
          </p>
          <p className="text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-4">
            This usually happens when the stop source is switched between{' '}
            <strong>Org Chart</strong> and <strong>Custom</strong> after paths have
            already been built — the two use different anchors, so stops drawn
            against one don&rsquo;t survive the other. If you meant to switch,
            reload and change it before adding stops.
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium rounded transition-colors"
            >
              <RotateCcw className="h-4 w-4" />
              Reload the designer
            </button>
          </div>
          {error && (
            <details className="mt-4">
              <summary className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] cursor-pointer">
                Technical details
              </summary>
              <pre className="mt-1 text-xs bg-[var(--hover-tint)] dark:bg-[var(--surface-raised)] p-2 rounded overflow-auto max-h-40">
                {error.toString()}
              </pre>
            </details>
          )}
        </div>
      </div>
    </div>
  );
}
